'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  FloatingList,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';
import type React from 'react';

import { css, cx } from '@/styled-system/css';

import { MenuFloatingContext } from './context/menu-floating.context';
import { MenuRootContext } from './context/menu-root.context';
import { extractMenuChildren } from './extract-menu-children';
import { menuContentPositionerStyles, menuContentRecipe } from './styles/menu-content.styles';
import { menuListStyles } from './styles/menu-list.styles';
import type { MenuContentPropsInterface } from './types/menu-content.types';

/**
 * @since 10.0.0
 */
export const MenuContent = forwardRef<HTMLDivElement, MenuContentPropsInterface>(
  (props: MenuContentPropsInterface, forwardedRef) => {
    const { dividers, menu } = useContext(MenuRootContext);
    const floating = useContext(MenuFloatingContext);
    const {
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const contentPositionerStyles = css(menuContentPositionerStyles);
    const contentStyles = css(menuContentRecipe.raw({ dividers }), cssProp);

    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['content'],
      modal: false,
      guards: false,
      visuallyHiddenDismiss: 'Dismiss menu',
      ...(menu.openedByPointerRef.current && {
        initialFocus: menu.refs.domReference as React.MutableRefObject<HTMLElement | null>,
      }),
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([menu.refs.setFloating, forwardedRef]);

    const {
      'role': _role,
      'tabIndex': _tabIndex,
      'aria-orientation': _ariaOrientation,
      'aria-labelledby': floatingLabelledBy,
      ...floatingProps
    } = floating.getFloatingProps(rest);

    const { heading, rest: body, hasItems } = extractMenuChildren(children);

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <FloatingList elementsRef={menu.elementsRef}>
          <div
            ref={mergedRefs}
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="Menu.ContentPositioner"
            className={cx('c11n', contentPositionerStyles)}
            style={{
              ...menu.floatingStyles,
              visibility: menu.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            }}
            {...floatingProps}
            aria-labelledby={menu.labelId ?? (floatingLabelledBy as string | undefined)}
          >
            <div
              data-c11n-version={__C11N_VERSION__}
              data-c11n-component="Menu.Content"
              className={cx('c11n', contentStyles, className)}
              style={{ ...menu.floatingTransitionStyles, ...style }}
            >
              {heading}
              {hasItems ? (
                <ul className={cx('c11n', css(menuListStyles))} aria-labelledby={menu.labelId}>
                  {body}
                </ul>
              ) : (
                body
              )}
            </div>
          </div>
        </FloatingList>
      </FloatingFocusManager>
    );
  },
);

MenuContent.displayName = 'Menu.Content';
